/**
 * THE THREE THINGS THAT GO WRONG, AS PAGES A PLAYER CAN READ.
 *
 * ── WHAT ALREADY EXISTED, AND WHY THIS DOES NOT REPLACE IT ──────────────────
 * `routes.ts` shipped `renderNotFound`: a real `<main>` with a heading, a
 * sentence and a link to the home page, in eight languages, and `main.ts`'s
 * router already fell back to it for every unclaimed path. That page was
 * correct and it is still the 404 — this module CALLS it and adds the one line
 * it was missing (whose fault this is), rather than growing a second 404 that
 * would drift from the first. Three tests already assert its heading; they hold.
 *
 * What did NOT exist was any page at all for a SERVER failure. When the API was
 * down the shell carried on: `fetchSiteInfo` swallowed the error and returned a
 * blank company, `bootstrapSession` answered "no user", and the player got the
 * landing page or a meta screen full of zeroes — an application pretending to
 * work against a server that was not there. That is the gap this fills, and the
 * reachability is in `main.ts`: `probeApi()` at boot, `render()`'s first branch,
 * and the Retry button that re-probes.
 *
 * ── WHAT AN ERROR SCREEN OWES THE PLAYER ────────────────────────────────────
 *   1. WHAT HAPPENED, in a sentence. The status code is a footnote at the
 *      bottom (`detail`) because it is useful when they report the problem and
 *      useless as a headline — nobody knows what 502 means and nobody should
 *      have to.
 *   2. WHOSE FAULT IT IS. A mistyped URL is nobody's failure and a 502 is ours;
 *      a player who is not told assumes they broke it, or that their progress
 *      is gone. `our_fault` says the saved state is untouched, which is true —
 *      the server holds it and a gateway error never reached it.
 *   3. A WAY OUT THAT WORKS. For the 404 that is the home LINK the existing
 *      page already draws — a real route, never `history.back()`, which on a
 *      cold visit from a shared link goes nowhere.
 *   4. A WAY TO RETRY, for a server error only, because a 502 is usually
 *      transient. The outage pages deliberately draw NO home link: while the
 *      API is unreachable every route renders this same page, so a link home
 *      would be a control that visibly does nothing. Retry is the exit.
 *
 * NOTHING IS INVENTED HERE. No support address, no ticket id, no incident
 * number, no "back in 10 minutes" — the project has no support channel
 * configured, so this page does not pretend it has one.
 *
 * ── LAYER (ADR-0005) ────────────────────────────────────────────────────────
 * SHELL, and cheap enough to be: `t`, three helpers from `dom.ts`, and
 * `routes.ts`, all of which the entry point already holds. No pixi, nothing
 * under `engine/match/**`, no art. Nothing here moves, so there is no
 * `prefers-reduced-motion` question to answer — a page that appears when the
 * game is already broken is the last place to put a flash or a spinner.
 */
import { t } from '../i18n';
import { TAP_TARGET, el, replaceChildren } from './dom';
import { skinButton } from '../ui/button';
import { createSlotIcon } from '../ui/icon-registry';
import { attachPress } from '../ui/press';
import { renderNotFound } from './routes';

/**
 * WHICH PAGE. Named by what the player experiences, not by the number:
 *
 *   `not_found`     404 — the address does not name a page. Their side, and
 *                   harmless. Extends `renderNotFound`.
 *   `server`        500 / 502, and a request that never completed at all
 *                   (`status` 0: DNS, a dropped connection, the 15s timeout).
 *                   Our side, and usually transient — so, retryable.
 *   `unavailable`   503 / 505. Also ours, but a REFUSAL rather than a failure:
 *                   the server (or the proxy in front of it) is up and is
 *                   declining to serve — maintenance, a restart, a version it
 *                   will not speak. Same shape as `server`, different sentence,
 *                   because "we broke" and "we are closed" are not the same
 *                   news and a player who is told the wrong one either waits
 *                   for nothing or reloads for nothing.
 */
export type ErrorScreenKind = 'not_found' | 'server' | 'unavailable';

export interface ErrorScreenOptions {
  readonly kind: ErrorScreenKind;
  /**
   * The HTTP status, when there was one. `0` means the request never got an
   * answer, and is rendered as no detail line at all rather than as "HTTP 0",
   * which is not a thing and reads as a bug in the error page itself.
   */
  readonly status?: number;
  /**
   * Retry. Ignored for `not_found` — retrying a URL that does not exist gives
   * the same answer forever, and a button that cannot succeed is worse than no
   * button. The screen disables its own control and says so until the caller
   * re-renders, so a slow probe does not look like a dead button.
   */
  readonly onRetry?: () => void;
  /**
   * This render IS THE ANSWER TO A RETRY THAT FAILED.
   *
   * Without it a failed retry is silent to a screen reader: the button says
   * "Trying…", the caller re-renders the identical page, and the old subtree
   * (button and focus with it) is thrown away — focus lands on `<body>` and
   * nothing is spoken, so the player is told the press did nothing when in
   * fact it did and the answer was still "no". With it the page fills a
   * `role="status"` region a beat after insertion (a live region populated in
   * the same frame it is inserted is not reliably announced) and moves focus
   * to the heading, which is the top of the message and one Tab from Retry.
   *
   * The announcement is the page's own title, not a new sentence: "we could
   * not reach the game" is exactly the news, in eight languages already.
   */
  readonly retryFailed?: boolean;
}

/**
 * Map a status onto a page. Exported because `main.ts` decides at the probe and
 * the test asserts the boundaries — 500 and 502 are a failure, 503 and 505 are
 * a refusal, and 0 (no response at all) is a failure.
 */
export function errorKindForStatus(status: number): ErrorScreenKind {
  return status === 503 || status === 505 ? 'unavailable' : 'server';
}

/** The status line, or nothing. Never the headline. */
function detailLine(status: number | undefined): HTMLElement | null {
  if (status === undefined || status <= 0) return null;
  return el('p', {
    class: 'text-[12px] text-text-dim',
    'data-error-status': String(status),
    text: t('errors.error_page.detail', { status }),
  });
}

/**
 * Draw an error page into `root`. Returns its teardown, like every screen.
 *
 * The two outage pages reuse the 404's box exactly — same `<main id="main">`,
 * same `min-h-[60vh]` centred column, same `font-heading text-2xl text-gold`
 * heading — so the three pages are one design and the skip link still lands.
 */
export function renderErrorScreen(root: HTMLElement, options: ErrorScreenOptions): () => void {
  if (options.kind === 'not_found') {
    // The EXISTING page, extended: it already draws the heading, the sentence
    // and the link home. All that was missing is the reassurance.
    const teardown = renderNotFound(root);
    const main = root.querySelector('main');
    // `insertBefore` the link paragraph, not `append`: "this is not your fault"
    // belongs with the explanation, above the way out, or the player reads the
    // exit first and never sees it.
    const cause = el('p', {
      class: 'text-text-dim',
      'data-error-cause': 'not_found',
      text: t('errors.error_page.not_found_cause'),
    });
    if (main !== null) main.insertBefore(cause, main.children[2] ?? null);
    return teardown;
  }

  const ours = options.kind === 'unavailable';
  const retry = el('button', {
    type: 'button',
    class:
      `${TAP_TARGET} justify-center rounded border border-border bg-surface px-4 ` +
      'text-text hover:border-gold disabled:opacity-60',
    'data-action': 'retry',
  }, [
    createSlotIcon(document, 'action-retry', { size: 20, class: 'rga-btn__icon mr-1.5' }),
    el('span', { text: t('errors.error_page.retry') }),
  ]);
  skinButton(retry, { variant: 'primary' });
  attachPress(retry, { onPress: () => {
    if (retry.disabled) return;
    // Said out loud, not just disabled: a control that greys out with no other
    // change reads as "broken", which on this page of all pages is the wrong
    // message. The caller re-renders on the answer, which replaces this node.
    retry.disabled = true;
    retry.setAttribute('aria-busy', 'true');
    retry.classList.add('is-loading');
    retry.textContent = t('errors.error_page.retrying');
    options.onRetry?.();
  } });

  const title = t(ours ? 'errors.error_page.unavailable_title' : 'errors.error_page.server_title');
  // `tabindex="-1"` so focus can be MOVED here after a failed retry without
  // putting the heading in the tab order for everybody else.
  const heading = el('h1', {
    class: 'font-heading text-2xl text-gold',
    tabindex: '-1',
    text: title,
  });
  // Present on every outage render, empty until there is something to say: a
  // live region has to be in the document BEFORE its text changes or the
  // change is not a change and nothing is announced.
  const status = el('p', {
    class: 'sr-only',
    role: 'status',
    'aria-live': 'polite',
    'data-retry-status': '',
  });

  const main = el('main', {
    id: 'main',
    class: 'mx-auto flex min-h-[60vh] w-full max-w-3xl flex-col justify-center gap-4 px-4 py-16',
    'data-error-screen': options.kind,
  }, [
    heading,
    el('p', {
      class: 'text-text-dim',
      text: t(ours ? 'errors.error_page.unavailable_body' : 'errors.error_page.server_body'),
    }),
    el('p', {
      class: 'text-text-dim',
      'data-error-cause': 'ours',
      text: t('errors.error_page.our_fault'),
    }),
    status,
    options.onRetry === undefined ? null : el('p', {}, [retry]),
    detailLine(options.status),
  ]);

  replaceChildren(root, main);

  let announce: ReturnType<typeof setTimeout> | null = null;
  if (options.retryFailed === true) {
    // Focus first and synchronously: the node the player's focus was on has
    // just been discarded, and every frame it spends on `<body>` is a frame in
    // which the arrow keys scroll the document instead of reading the page.
    heading.focus();
    announce = setTimeout(() => {
      announce = null;
      status.textContent = title;
    }, 100);
  }

  return () => {
    if (announce !== null) clearTimeout(announce);
    replaceChildren(root);
  };
}
